import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import CheckInTokenTriangleIcon from "@/assets/icons/CheckInTokenTriangleIcon.svg";
import DriverProfileCardWatermark from "@/assets/backgrounds/DriverProfileCardWatermark.svg";
import { pickInitials } from "@/lib/utils";
import i18n from "@/lang/i18n";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { LogOutIcon } from "lucide-react";

interface CheckInDriverProfileCardProps {
    name: string,
    driverId: string,
    hub: string,
    checkInToken: string,
    handleLogout: () => void
}

const CheckInDriverProfileCard: React.FC<CheckInDriverProfileCardProps> = ({ handleLogout, name, driverId, hub, checkInToken }) => {

    return (
        <div className="relative w-full rounded-xl md:rounded-2xl lg:rounded-[23px] mb-10 md:mb-8">
            <img 
                className="w-full h-[250px] object-cover rounded-2xl lg:rounded-[30px]" 
                alt="bg-driver-profile-card-watermark" 
                src={DriverProfileCardWatermark} 
            />
            <div className="absolute inset-0 w-full pt-4 md:pt-6 lg:pt-[23px] pb-2 md:pb-3 lg:pb-[12px] px-2 md:px-4 lg:px-[9px] flex flex-col">
                <h5 className="text-white font-semibold leading-10 text-2xl md:text-3xl lg:text-[32px] text-primary pl-4 md:pl-6 lg:pl-[22px]">
                    {i18n.t("welcome")}!
                </h5>
                
                <div className="flex-1 flex flex-col justify-between">
                    <div className="flex items-start pl-2 md:pl-4 lg:pl-[14px] pt-2 md:pt-4 lg:pt-[10px]">
                        <Popover>
                            <PopoverTrigger>
                                <Avatar className="w-14 h-14 md:w-16 md:h-16 lg:w-[62px] lg:h-[62px] text-xl md:text-2xl lg:text-[32px]">
                                    <AvatarImage />
                                    <AvatarFallback>{pickInitials(name)}</AvatarFallback>
                                </Avatar>
                            </PopoverTrigger>
                            <PopoverContent className="w-36 p-2 flex flex-col items-center">
                                <Button onClick={handleLogout} variant="ghost" className="w-full flex items-center justify-center gap-2">
                                    <LogOutIcon className="w-4 h-4" />
                                    Logout
                                </Button>
                            </PopoverContent>
                        </Popover>
                        <div className="pl-3 md:pl-4 lg:pl-[10px] flex flex-col justify-center gap-2">
                            <p className="font-semibold text-white text-xl md:text-lg lg:text-[16px] max-w-[200px] md:max-w-[240px] truncate leading-[27px]">
                                {name} - {driverId}
                            </p>
                            <p className="font-medium text-secondary text-lg md:text-base lg:text-[14px] truncate leading-[20px]">
                                {hub}
                            </p>
                        </div>
                    </div>
                    
                    {/* Check-In Token Section */}
                    <div className="rounded-xl md:rounded-2xl lg:rounded-[23px] bg-white w-full py-4 md:py-4">
                        <div className="flex flex-col items-center justify-center gap-2 h-full">
                            <div className="flex items-center gap-2">
                                <img
                                    className="w-8 h-8 md:w-10 md:h-10"
                                    alt="Icon"
                                    src={CheckInTokenTriangleIcon}
                                />
                                <p className="text-lg md:text-base lg:text-lg text-secondary">
                                    Check In Token
                                </p>
                            </div>
                            <p className="text-primary text-2xl md:text-2xl lg:text-3xl font-bold tracking-wide">
                                {checkInToken}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckInDriverProfileCard;
